import { computed, nextTick, ref, watch } from 'vue'
import type { Component, ComputedRef, Ref } from 'vue'

import type { SlashCommandOption } from '~/interfaces/SlashCommandOption'

interface TypedCommandMatch {
  /** The matched option's normalized value. */
  scope: string
  /** The search term typed after the command. */
  term: string
}

interface SlashCommandPaletteOptions {
  /** The scopes the palette lists. */
  options: () => readonly SlashCommandOption[]
  /** Receives the scope chosen from the palette, after the palette resets. */
  onSelect: (value: string) => void
  /** Whether the field currently accepts interaction; the palette closes and ignores selection while it does not. */
  isInteractive: () => boolean
  /** The search input's component ref; the palette focuses its element after an option is chosen or dismissed. */
  inputRef: Readonly<Ref<{ inputRef: HTMLInputElement | null } | null>>
}

interface UseSlashCommandPaletteReturn {
  dismissPrompt: () => void
  filteredCommandOptions: ComputedRef<
    { icon: Component | undefined; label: string; value: string }[]
  >
  handleKeydown: (event: KeyboardEvent) => void
  handleOptionKeydown: (event: KeyboardEvent) => void
  highlightOption: (value: string) => void
  highlightedOptionValue: Ref<string>
  isInteractionAllowed: ComputedRef<boolean>
  isPaletteOpen: ComputedRef<boolean>
  matchTypedCommand: (rawValue: string) => TypedCommandMatch | undefined
  resetPalette: () => void
  selectOption: (value: string) => void
}

/** Open/filter/highlight state and keyboard navigation of a search input's slash-command palette. */
export function useSlashCommandPalette({
  options,
  onSelect,
  isInteractive,
  inputRef
}: SlashCommandPaletteOptions): UseSlashCommandPaletteReturn {
  const focusInput = () => inputRef.value?.inputRef?.focus()
  const commandPrompt = ref('')
  const isOpenRequested = ref(false)
  const isInteractionAllowed = computed(isInteractive)
  const isPaletteOpen = computed(() => isOpenRequested.value && isInteractionAllowed.value)
  const highlightedOptionValue = ref('')

  const commandOptions = computed(() =>
    options().map((option) => ({
      icon: option.icon,
      label: option.label ?? '',
      value: option.value.trim().toLowerCase()
    }))
  )

  const filteredCommandOptions = computed(() => {
    const query = commandPrompt.value.trim().toLowerCase()
    if (!query) return commandOptions.value
    return commandOptions.value.filter(
      (option) => option.value.includes(query) || option.label.toLowerCase().includes(query)
    )
  })

  const hasSelectableOptions = computed(
    () => isPaletteOpen.value && filteredCommandOptions.value.length > 0
  )

  const findCommand = (command: string) =>
    commandOptions.value.find((option) => option.value === command)

  const openWithPrompt = (prompt: string) => {
    commandPrompt.value = prompt
    isOpenRequested.value = true
    highlightedOptionValue.value = filteredCommandOptions.value[0]?.value ?? ''
  }

  const closePalette = () => {
    isOpenRequested.value = false
    highlightedOptionValue.value = ''
  }

  /** Closes the palette and forgets the typed prompt, keeping the highlight untouched. */
  const dismissPrompt = () => {
    isOpenRequested.value = false
    commandPrompt.value = ''
  }

  const resetPalette = () => {
    closePalette()
    commandPrompt.value = ''
  }

  // Losing interaction forgets the open request, prompt and highlight so re-enabling starts closed.
  watch(
    isInteractionAllowed,
    (allowed) => {
      if (!allowed) resetPalette()
    },
    { flush: 'sync' }
  )

  const highlightOption = (value: string) => {
    highlightedOptionValue.value = value
  }

  const selectOption = (value: string) => {
    if (!isInteractionAllowed.value) return

    resetPalette()
    onSelect(value)
    // The chosen option button leaves the DOM with the palette, so focus returns to the input.
    void nextTick(focusInput)
  }

  /** A leading word naming an option closes the palette and returns its scope; else it filters. */
  const matchTypedCommand = (rawValue: string): TypedCommandMatch | undefined => {
    const [rawCommand = '', ...rest] = rawValue.slice(1).trimStart().split(/\s+/)
    const command = rawCommand.toLowerCase()
    const matched = command ? findCommand(command) : undefined

    if (matched) {
      dismissPrompt()
      return { scope: matched.value, term: rest.join(' ').trim() }
    }

    openWithPrompt(command)
    return undefined
  }

  /** Moves the highlight one listed option down (1) or up (-1), wrapping at either end. */
  const moveHighlight = (direction: 1 | -1) => {
    const listed = filteredCommandOptions.value
    const currentIndex = listed.findIndex((option) => option.value === highlightedOptionValue.value)
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + direction + listed.length) % listed.length

    highlightedOptionValue.value = listed[nextIndex]?.value ?? ''
  }

  const isListedOption = (value: string): boolean =>
    filteredCommandOptions.value.some((option) => option.value === value)

  /** Enter picks the highlighted option while it is still listed, or else the first listed one. */
  const selectHighlightedOption = (event: KeyboardEvent) => {
    const value = isListedOption(highlightedOptionValue.value)
      ? highlightedOptionValue.value
      : (filteredCommandOptions.value[0]?.value ?? '')
    if (!value) return

    event.preventDefault()
    selectOption(value)
  }

  const moveHighlightFromKey = (event: KeyboardEvent, direction: 1 | -1) => {
    event.preventDefault()
    moveHighlight(direction)
  }

  const navigationKeys = new Map<string, (event: KeyboardEvent) => void>([
    [
      'ArrowDown',
      (event) => {
        moveHighlightFromKey(event, 1)
      }
    ],
    [
      'ArrowUp',
      (event) => {
        moveHighlightFromKey(event, -1)
      }
    ],
    ['Enter', selectHighlightedOption]
  ])

  /** Arrow keys and Enter suppress native behavior only while the palette lists something. */
  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closePalette()
      return
    }
    if (!hasSelectableOptions.value) return

    navigationKeys.get(event.key)?.(event)
  }

  const isElement = (target: EventTarget | null): target is HTMLElement =>
    target instanceof HTMLElement

  /** Moves focus to the option button that follows the highlight move, found among the focused button's siblings. */
  const focusHighlightedOption = (focused: EventTarget | null) => {
    if (!isElement(focused)) return

    const target =
      Array.from(focused.parentElement?.children ?? []).find(
        (child) =>
          isElement(child) && child.dataset['commandValue'] === highlightedOptionValue.value
      ) ?? null
    if (isElement(target)) target.focus()
  }

  /** Keyboard navigation and Enter start from the focused option, not from a hovered one. */
  const highlightFocusedOption = (event: KeyboardEvent) => {
    if (isElement(event.currentTarget)) {
      highlightedOptionValue.value = event.currentTarget.dataset['commandValue'] ?? ''
    }
  }

  const moveFromFocusedOption = (event: KeyboardEvent, direction: 1 | -1) => {
    highlightFocusedOption(event)
    moveHighlightFromKey(event, direction)
    focusHighlightedOption(event.currentTarget)
  }

  // Enter is handled here because an ancestor may cancel the button's native activation.
  const optionKeys = new Map<string, (event: KeyboardEvent) => void>([
    [
      'Escape',
      (event) => {
        event.preventDefault()
        closePalette()
        focusInput()
      }
    ],
    [
      'Enter',
      (event) => {
        highlightFocusedOption(event)
        selectHighlightedOption(event)
      }
    ],
    [
      'ArrowDown',
      (event) => {
        moveFromFocusedOption(event, 1)
      }
    ],
    [
      'ArrowUp',
      (event) => {
        moveFromFocusedOption(event, -1)
      }
    ]
  ])

  /** Option buttons share the input's palette keys; Escape also returns focus to the input. */
  const handleOptionKeydown = (event: KeyboardEvent) => {
    optionKeys.get(event.key)?.(event)
  }

  return {
    dismissPrompt,
    filteredCommandOptions,
    handleKeydown,
    handleOptionKeydown,
    highlightOption,
    highlightedOptionValue,
    isInteractionAllowed,
    isPaletteOpen,
    matchTypedCommand,
    resetPalette,
    selectOption
  }
}
